import { useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers } from "@/hooks/use-users";
import {
  useRenameAccessGroup,
  useDeleteAccessGroup,
  useAddAccessGroupMember,
  useRemoveAccessGroupMember,
  type AccessGroup,
} from "@/hooks/use-access-groups";

/** Én gruppe: navn, medlemmer og sletting. */
export function AccessGroupRow({ group }: { group: AccessGroup }) {
  const { user: authUser } = useAuth();
  const { data: alleBrukere = [] } = useUsers();
  const endreNavn = useRenameAccessGroup();
  const slett = useDeleteAccessGroup();
  const leggTil = useAddAccessGroupMember();
  const fjern = useRemoveAccessGroupMember();

  const [navn, setNavn] = useState(group.name);
  const [plukkerApen, setPlukkerApen] = useState(false);

  const medlemmer = new Set(group.member_ids);
  const ledige = alleBrukere.filter((u) => !medlemmer.has(u.id));
  const endret = navn.trim() !== group.name && navn.trim().length > 0;

  async function slettGruppa() {
    const antall = group.member_ids.length;
    const advarsel =
      antall > 0
        ? `Slett «${group.name}»? ${antall} ${antall === 1 ? "person" : "personer"} mister tilgangen gruppa gir.`
        : `Slett «${group.name}»?`;
    if (!window.confirm(advarsel)) return;
    await slett.mutateAsync({ id: group.id });
  }

  return (
    <AccordionItem value={group.id}>
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <div className="flex flex-1 items-center gap-3">
          <span className="text-sm font-medium">{group.name}</span>
          <span className="ml-auto mr-3 text-xs text-muted-foreground">
            {group.member_ids.length}{" "}
            {group.member_ids.length === 1 ? "medlem" : "medlemmer"}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        <div className="space-y-3">
          <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_auto_auto]">
            <div>
              <Label className="text-xs" htmlFor={`gruppenavn-${group.id}`}>
                Navn
              </Label>
              <Input
                id={`gruppenavn-${group.id}`}
                value={navn}
                onChange={(e) => setNavn(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <Button
              size="sm"
              className="h-8"
              onClick={() => endreNavn.mutateAsync({ id: group.id, name: navn })}
              disabled={!endret || endreNavn.isPending}
            >
              Lagre navn
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-destructive hover:text-destructive"
              onClick={slettGruppa}
              disabled={slett.isPending}
              aria-label={`Slett gruppa ${group.name}`}
            >
              <SrIcon name="trash2" className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div>
            <Label className="mb-2 block text-xs">Medlemmer</Label>
            <div className="flex flex-wrap gap-2">
              {group.member_ids.map((id) => {
                const bruker = alleBrukere.find((u) => u.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 rounded border bg-muted/40 px-2 py-1 text-xs"
                  >
                    {bruker?.full_name ?? id.slice(0, 8)}
                    <button
                      type="button"
                      onClick={() =>
                        fjern.mutateAsync({ groupId: group.id, userId: id })
                      }
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Fjern ${bruker?.full_name ?? "medlem"} fra ${group.name}`}
                    >
                      <SrIcon name="x" className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
              <Popover open={plukkerApen} onOpenChange={setPlukkerApen}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs">
                    <SrIcon name="plus" className="mr-1 h-3 w-3" />
                    Legg til
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Søk ansatte …" />
                    <CommandList>
                      <CommandEmpty>Ingen flere å legge til</CommandEmpty>
                      <CommandGroup>
                        {ledige.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={u.full_name ?? u.email ?? u.id}
                            onSelect={async () => {
                              if (!authUser?.id) return;
                              await leggTil.mutateAsync({
                                groupId: group.id,
                                userId: u.id,
                                addedBy: authUser.id,
                              });
                              setPlukkerApen(false);
                            }}
                            className="text-xs"
                          >
                            {u.full_name ?? u.email ?? u.id}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
